import type {
	Offering,
	OfferingProPhotos,
	OfferingTypes,
	RemappedOffering,
	RemappedOfferingProPhotos,
	RemappedService,
	RemappedServiceProPhotos,
	Service,
	ServiceBaseAnswer,
	ServiceDiffsMap,
	ServiceGalleryImage,
	ServiceProPhotos,
	ServiceStatus
} from '@/features/service-vetting/types.ts'
import { objectValues } from '@/helpers/utils'
import type { TKey } from '@/translations/i18n'
import type {
	ServiceMerchandisingInputSchema,
	ServiceMerchandisingOutputSchema
} from './form-schemas'

// Maps question_key_name to accumulator key for expertise remapping
const EXPERTISE_KEY_MAP: Record<string, string> = {
	experience_years: 'experience_years',
	summary: 'summary',
	highlights: 'highlights',
	inspiration: 'inspiration',
	background: 'background',
	highlight_expertise: 'highlight_expertise',
	links: 'links',
	profile_photo: 'profile_photo',
	location: 'location',
	cities: 'cities',
	host_experience_title: 'host_experience_title',
	host_experience_description: 'host_experience_description',
	host_education_title: 'host_education_title',
	host_education_description: 'host_education_description',
	host_career_highlight_title: 'host_career_highlight_title',
	host_career_highlight_description: 'host_career_highlight_description',
	service_title: 'service_title',
	service_description: 'service_description'
}

const ARRAY_PREVIOUS_ANSWER_KEYS = new Set(['links', 'cities'])

export const remapServiceExpertise = (expertise: Service['expertise']) =>
	expertise.question_answer.reduce<
		RemappedService['expertise']['question_answer']
	>(
		(acc, currentValue) => {
			const accKey = EXPERTISE_KEY_MAP[currentValue.question_key_name]
			if (accKey && currentValue.answer != null) {
				const defaultPrevious = ARRAY_PREVIOUS_ANSWER_KEYS.has(accKey)
					? []
					: ''
				;(acc as Record<string, unknown>)[accKey] = {
					id: currentValue.id,
					answer: currentValue.answer,
					flags: currentValue.flags,
					previous_answer:
						currentValue.previous_answer || defaultPrevious
				}
			}
			return acc
		},
		{} as RemappedService['expertise']['question_answer']
	)

export const remapServiceOfferings = (
	offering: Offering
): RemappedOffering['question_answer'] => {
	const defaultAnswer: ServiceBaseAnswer = {
		id: 0,
		answer: '',
		flags: [],
		previous_answer: ''
	}

	const findAnswer = (key: string): ServiceBaseAnswer => {
		const item = offering.question_answer.find(
			(qa) => qa.question_key_name === key
		)
		if (!item) {
			return defaultAnswer
		}
		return {
			id: item.id,
			answer: item.answer,
			flags: item.flags,
			previous_answer: item.previous_answer ?? ''
		}
	}

	const findOptionalAnswer = (key: string): ServiceBaseAnswer | undefined => {
		const item = offering.question_answer.find(
			(qa) => qa.question_key_name === key
		)
		if (!item) {
			return undefined
		}
		return {
			id: item.id,
			answer: item.answer,
			flags: item.flags,
			previous_answer: item.previous_answer ?? ''
		}
	}

	return {
		title: findAnswer('title'),
		price_per_guest: findAnswer('price_per_guest'),
		price_per_group: findOptionalAnswer('price_per_group'),
		currency: findAnswer('currency'),
		duration: findAnswer('duration'),
		description: findAnswer('description'),
		main_photo: findOptionalAnswer('main_photo'),
		improved_title: findAnswer('improved_title'),
		improved_description: findAnswer('improved_description')
	}
}

export const remapServiceForVetting = (service: Service): RemappedService => {
	return {
		...service,
		expertise: {
			...service.expertise,
			question_answer: remapServiceExpertise(service.expertise)
		},
		offerings: service.offerings.map((offering) => ({
			...offering,
			question_answer: remapServiceOfferings(offering)
		}))
	}
}

export const groupByCountry = (
	cities: Service['submission_cities']
): Record<string, string[]> => {
	return cities.reduce<Record<string, string[]>>((acc, entry) => {
		const country = entry.city.country
		const name = entry.city.name

		if (!acc[country]) {
			acc[country] = []
		}
		acc[country].push(name)

		return acc
	}, {})
}

const getExpertiseErrors = (service: RemappedService): TKey[] => {
	if (!service.expertise.status) {
		return ['vetting:criteria.service.validation_errors.make_a_decision']
	}
	if (
		service.expertise.status === 'declined' &&
		service.expertise.flags &&
		service.expertise.flags.length === 0
	) {
		return [
			'vetting:criteria.service.validation_errors.select_at_least_one_flag'
		]
	}
	if (
		service.expertise.status === 'back_to_draft' &&
		objectValues(service.expertise.question_answer).every(
			(value) => !value?.flags || value.flags.length === 0
		)
	) {
		return [
			'vetting:criteria.service.validation_errors.flag_at_least_one_field'
		]
	}
	return []
}

const getOfferingError = (
	offering: RemappedService['offerings'][number]
): TKey | null => {
	if (!offering.status) {
		return 'vetting:criteria.service.validation_errors.make_a_decision'
	}
	if (offering.status === 'declined' && offering.flags?.length === 0) {
		return 'vetting:criteria.service.validation_errors.select_at_least_one_flag'
	}
	if (
		offering.status === 'back_to_draft' &&
		objectValues(offering.question_answer).every(
			(value) => value?.flags?.length === 0
		)
	) {
		return 'vetting:criteria.service.validation_errors.flag_at_least_one_field'
	}
	return null
}

const getItemErrors = (
	service: RemappedService
): { itemId: number; error: TKey }[] => {
	const itemErrors: { itemId: number; error: TKey }[] = []
	for (const offering of service.offerings) {
		const error = getOfferingError(offering)
		if (error) {
			itemErrors.push({ itemId: offering.id, error })
		}
	}
	return itemErrors
}

export const checkErrors = ({
	service,
	images
}: {
	service: RemappedService
	images: ServiceGalleryImage[]
}) => {
	const expertiseErrors = getExpertiseErrors(service)
	const galleryErrors: TKey[] = []
	const rejectedImagesErrors: TKey[] = []
	const remapGallery = images.filter(
		(img) =>
			!(
				img.is_offering_image ||
				img.is_host_approval_needed ||
				img.is_cover
			)
	)

	const itemErrors =
		service.expertise.status !== 'declined' ? getItemErrors(service) : []

	if (service.expertise.status !== 'declined') {
		if (remapGallery.some((value) => value.is_approved === null)) {
			galleryErrors.push(
				'vetting:criteria.service.validation_errors.select_accept_reject'
			)
		}
		if (
			remapGallery
				.filter((value) => value.is_approved === false)
				.some((value) => value.flags.length === 0)
		) {
			rejectedImagesErrors.push(
				'vetting:criteria.service.validation_errors.select_flag_for_rejected_image'
			)
		}
	}
	return {
		errors: {
			expertiseErrors,
			itemErrors,
			galleryErrors,
			rejectedImagesErrors
		},
		isValid: objectValues({
			expertiseErrors,
			itemErrors,
			galleryErrors,
			rejectedImagesErrors
		}).every((error) => error.length === 0)
	}
}

//merch copy input form defaultValues remapping
export const remapMerchCopyInputForm = (
	service: RemappedService
): ServiceMerchandisingInputSchema => {
	const qa = service.expertise.question_answer
	return {
		host_name: service.host.first_name ?? '',
		host_experience: qa.experience_years?.answer,
		host_experience_summary: qa.summary?.answer,
		host_experience_highlight: qa.highlights?.answer,
		host_experience_background: qa.background?.answer,
		service_title: qa.inspiration?.answer ?? '',
		highlight_expertise: qa.highlight_expertise?.answer,
		offerings: service.offerings.map((offering) => ({
			id: offering.id,
			title: offering.question_answer.title?.answer,
			description: offering.question_answer.description?.answer,
			duration: offering.question_answer.duration?.answer,
			type: offering.type
		}))
	}
}

//merch copy AI-output form defaultValues remapping
export const remapMerchCopyAiSubmittedForm = (
	service: RemappedService
): ServiceMerchandisingOutputSchema => {
	const qa = service.expertise.question_answer
	return {
		host_experience_title: qa.host_experience_title?.answer ?? '',
		host_experience_description:
			qa.host_experience_description?.answer ?? '',
		host_education_title: qa.host_education_title?.answer ?? '',
		host_education_description: qa.host_education_description?.answer ?? '',
		host_career_highlight_title:
			qa.host_career_highlight_title?.answer ?? '',
		host_career_highlight_description:
			qa.host_career_highlight_description?.answer ?? '',
		service_title: qa.service_title?.answer ?? '',
		service_description: qa.service_description?.answer ?? '',
		offerings: service.offerings.map((offering) => ({
			id: offering.id,
			title: offering.question_answer.title?.answer ?? '',
			description: offering.question_answer.description?.answer ?? '',
			duration: offering.question_answer.duration?.answer ?? '',
			improved_title:
				offering.question_answer.improved_title?.answer ?? '',
			improved_description:
				offering.question_answer.improved_description?.answer ?? '',
			type: offering.type
		}))
	}
}

//merch copy host edits defaultValues remapping
export const remapMerchCopyHostEdits = (
	service: RemappedService
): ServiceMerchandisingOutputSchema => {
	const qa = service.expertise.question_answer
	return {
		host_experience_title: qa.host_experience_title?.previous_answer ?? '',
		host_experience_description:
			qa.host_experience_description?.previous_answer ?? '',
		host_education_title: qa.host_education_title?.previous_answer ?? '',
		host_education_description:
			qa.host_education_description?.previous_answer ?? '',
		host_career_highlight_title:
			qa.host_career_highlight_title?.previous_answer ?? '',
		host_career_highlight_description:
			qa.host_career_highlight_description?.previous_answer ?? '',
		service_title: qa.service_title?.previous_answer ?? '',
		service_description: qa.service_description?.previous_answer ?? '',
		offerings: service.offerings.map((offering) => ({
			id: offering.id,
			title: offering.question_answer.title?.previous_answer ?? '',
			description:
				offering.question_answer.description?.previous_answer ?? '',
			duration: offering.question_answer.duration?.previous_answer ?? '',
			improved_title:
				offering.question_answer.improved_title?.previous_answer ?? '',
			improved_description:
				offering.question_answer.improved_description
					?.previous_answer ?? '',
			type: offering.type
		}))
	}
}

export const remapMerchCopyHostEditsDiffs = (
	service: RemappedService
): ServiceDiffsMap => {
	const qa = service.expertise.question_answer
	return {
		service_title: {
			hasDiff:
				qa.service_title?.previous_answer !== qa.service_title?.answer,
			fieldSize: 'sm'
		},
		service_description: {
			hasDiff:
				qa.service_description?.previous_answer !==
				qa.service_description?.answer,
			fieldSize: 'md'
		},
		host_experience_title: {
			hasDiff:
				qa.host_experience_title?.previous_answer !==
				qa.host_experience_title?.answer,
			fieldSize: 'sm'
		},
		host_experience_description: {
			hasDiff:
				qa.host_experience_description?.previous_answer !==
				qa.host_experience_description?.answer,
			fieldSize: 'md'
		},
		host_education_title: {
			hasDiff:
				qa.host_education_title?.previous_answer !==
				qa.host_education_title?.answer,
			fieldSize: 'sm'
		},
		host_education_description: {
			hasDiff:
				qa.host_education_description?.previous_answer !==
				qa.host_education_description?.answer,
			fieldSize: 'md'
		},
		host_career_highlight_title: {
			hasDiff:
				qa.host_career_highlight_title?.previous_answer !==
				qa.host_career_highlight_title?.answer,
			fieldSize: 'sm'
		},
		host_career_highlight_description: {
			hasDiff:
				qa.host_career_highlight_description?.previous_answer !==
				qa.host_career_highlight_description?.answer,
			fieldSize: 'md'
		},
		offerings: service.offerings.map((offering) => [
			{
				hasDiff:
					offering.question_answer.improved_title?.previous_answer !==
					offering.question_answer.improved_title?.answer,
				fieldSize: 'sm'
			},
			{
				hasDiff:
					offering.question_answer.improved_description
						?.previous_answer !==
					offering.question_answer.improved_description?.answer,
				fieldSize: 'lg'
			}
		])
	}
}

export const getOfferingTypeLabel = (type: OfferingTypes): TKey => {
	if (!type) {
		return 'vetting:merch.service.offering_type.default'
	}

	const typeLabels: Record<OfferingTypes, TKey> = {
		basic: 'vetting:merch.service.offering_type.basic',
		standard: 'vetting:merch.service.offering_type.standard',
		luxe: 'vetting:merch.service.offering_type.luxe',
		exclusive: 'vetting:merch.service.offering_type.exclusive',
		other: 'vetting:merch.service.offering_type.other'
	}

	const label =
		typeLabels[type] || 'vetting:merch.service.offering_type.default'
	return label
}

// Maps questionKeyName to camelCase accumulator key for pro photos expertise remapping
const EXPERTISE_PRO_PHOTOS_KEY_MAP: Record<string, string> = {
	experience_years: 'experienceYears',
	summary: 'summary',
	highlights: 'highlights',
	inspiration: 'inspiration',
	background: 'background',
	highlight_expertise: 'highlightExpertise',
	links: 'links',
	profile_photo: 'profilePhoto',
	location: 'location',
	cities: 'cities',
	host_experience_title: 'hostExperienceTitle',
	host_experience_description: 'hostExperienceDescription',
	host_education_title: 'hostEducationTitle',
	host_education_description: 'hostEducationDescription',
	host_career_highlight_title: 'hostCareerHighlightTitle',
	host_career_highlight_description: 'hostCareerHighlightDescription',
	service_title: 'serviceTitle',
	service_description: 'serviceDescription'
}

const ARRAY_PREVIOUS_ANSWER_PRO_PHOTOS_KEYS = new Set(['links', 'cities'])

export const remapServiceExpertiseProPhotos = (
	expertise: ServiceProPhotos['expertise']
) =>
	expertise.questionAnswer.reduce<
		RemappedServiceProPhotos['expertise']['questionAnswer']
	>(
		(acc, currentValue) => {
			const accKey =
				EXPERTISE_PRO_PHOTOS_KEY_MAP[currentValue.questionKeyName]
			if (accKey && currentValue.answer != null) {
				const defaultPrevious =
					ARRAY_PREVIOUS_ANSWER_PRO_PHOTOS_KEYS.has(
						currentValue.questionKeyName
					)
						? []
						: ''
				;(acc as Record<string, unknown>)[accKey] = {
					id: 'id' in currentValue ? currentValue.id : 0,
					answer: currentValue.answer,
					flags: currentValue.flags,
					previousAnswer:
						currentValue.previousAnswer || defaultPrevious
				}
			}
			return acc
		},
		{} as RemappedServiceProPhotos['expertise']['questionAnswer']
	)

const OFFERING_PRO_PHOTOS_KEY_MAP: Record<string, string> = {
	title: 'title',
	price_per_guest: 'pricePerGuest',
	price_per_group: 'pricePerGroup',
	currency: 'currency',
	duration: 'duration',
	description: 'description',
	main_photo: 'mainPhoto',
	improved_title: 'improvedTitle',
	improved_description: 'improvedDescription'
}

export const remapServiceOfferingsProPhotos = (offering: OfferingProPhotos) =>
	offering.questionAnswer.reduce<RemappedOfferingProPhotos['questionAnswer']>(
		(acc, currentValue) => {
			const accKey =
				OFFERING_PRO_PHOTOS_KEY_MAP[currentValue.questionKeyName]
			if (accKey) {
				;(acc as Record<string, unknown>)[accKey] = {
					id: currentValue.id,
					answer: currentValue.answer,
					flags: currentValue.flags,
					previousAnswer: currentValue.previousAnswer ?? ''
				}
			}
			return acc
		},
		{} as RemappedOfferingProPhotos['questionAnswer']
	)

export const remapServiceForVettingProPhotos = (
	service: ServiceProPhotos
): RemappedServiceProPhotos => {
	return {
		...service,
		expertise: {
			...service.expertise,
			questionAnswer: remapServiceExpertiseProPhotos(service.expertise)
		},
		offerings: service.offerings.map((offering) => ({
			...offering,
			questionAnswer: remapServiceOfferingsProPhotos(offering)
		}))
	}
}

export const serviceVettingStatusToLabel = {
	archived_pending_host_approval:
		'vetting:criteria.service.service_status.archived_pending_host_approval',
	approval_required:
		'vetting:criteria.service.service_status.approval_required',
	approved: 'vetting:criteria.service.service_status.approved',
	declined: 'vetting:criteria.service.service_status.declined',
	in_progress: 'vetting:criteria.service.service_status.in_progress',
	under_review: 'vetting:criteria.service.service_status.under_review',
	pending_id_verification:
		'vetting:criteria.service.service_status.pending_id_verification',
	update_required: 'vetting:criteria.service.service_status.update_required',
	waitlisted: 'vetting:criteria.service.service_status.waitlisted',
	archived_update_required:
		'vetting:criteria.service.service_status.archived_update_required',
	completed: 'vetting:criteria.service.service_status.completed',
	pending_export: 'vetting:criteria.service.service_status.pending_export',
	pending_publishing:
		'vetting:criteria.service.service_status.pending_publishing',
	publishing_enabled:
		'vetting:criteria.service.service_status.publishing_enabled',
	pending_upgrade_api:
		'vetting:criteria.service.service_status.pending_upgrade_api',
	published_and_upgraded:
		'vetting:criteria.service.service_status.published_and_upgraded',
	pending_licensing:
		'vetting:criteria.service.service_status.pending_licensing'
} as const satisfies Record<ServiceStatus, TKey>
